import { useState, useEffect, useRef } from 'react';
import {
  X, ChevronRight, ChevronLeft, MapPin, Smartphone, Car, Map, Zap,
  RotateCcw, Crosshair,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import type { WizardMode } from '@/stores/campaignWizardStore';
import { useWizardStore } from '@/stores/campaignWizardStore';
import { getCampaignTypeByKey } from '@/lib/campaignTypeRegistry';
import { resolvePinStyle, PIN_STYLE_SVG, type PinStyle } from '@/lib/pinStyles';

// ─── Helpers ────────────────────────────────────────────────────────────────

function contrastText(hex: string): string {
  const c = hex.replace('#', '');
  if (c.length < 6) return '#ffffff';
  const r = parseInt(c.slice(0, 2), 16);
  const g = parseInt(c.slice(2, 4), 16);
  const b = parseInt(c.slice(4, 6), 16);
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.5 ? '#111827' : '#ffffff';
}

function lightenHex(hex: string, amount: number): string {
  const c = hex.replace('#', '');
  if (c.length < 6) return hex;
  const r = Math.min(255, parseInt(c.slice(0, 2), 16) + amount);
  const g = Math.min(255, parseInt(c.slice(2, 4), 16) + amount);
  const b = Math.min(255, parseInt(c.slice(4, 6), 16) + amount);
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

type PreviewTab = 'passenger' | 'driver' | 'pin';

interface LivePreviewProps {
  mode: WizardMode;
}

export default function LivePreview({ mode }: LivePreviewProps) {
  const store = useWizardStore(mode);
  const [tab, setTab] = useState<PreviewTab>('passenger');

  const {
    selectedBusiness, headline, body, ctaText, backgroundColor,
    templatePlacement, creativeMode, artImageUrl, uploadImageUrl,
    uploadVideoUrl, gallerySelectedLogoUrl, campaignTypes,
    pinLatOverride, pinLngOverride,
  } = store;

  // When multiple templates are selected, let the user cycle through
  // them in the preview to spot-check how each placement will look.
  const [previewIndex, setPreviewIndex] = useState(0);
  useEffect(() => {
    // Reset to first when the array changes underneath (e.g. on deselect)
    if (previewIndex >= campaignTypes.length) setPreviewIndex(0);
  }, [campaignTypes.length, previewIndex]);

  const currentTypeDef = campaignTypes.length > 0
    ? getCampaignTypeByKey(campaignTypes[previewIndex] || campaignTypes[0])
    : undefined;
  // Use the currently-previewed template's placement for the ad-tag logic
  const effectivePlacement = currentTypeDef?.placement || templatePlacement;

  const brandName = selectedBusiness?.name || 'Your Brand';
  const logoUrl = gallerySelectedLogoUrl || selectedBusiness?.logo_url || '';
  const heroUrl = artImageUrl || uploadImageUrl || selectedBusiness?.hero_image_url || '';
  const bg = backgroundColor || '#10b981';
  const fg = contrastText(bg);

  const tabs: { key: PreviewTab; label: string; icon: React.ReactNode }[] = [
    { key: 'passenger', label: 'Passenger', icon: <Smartphone className="w-3 h-3" /> },
    { key: 'driver',    label: 'Driver',    icon: <Car className="w-3 h-3" /> },
    { key: 'pin',       label: 'Map Pin',   icon: <Map className="w-3 h-3" /> },
  ];

  const isFullBleed = creativeMode === 'client_full_bleed';
  const isVideo = creativeMode === 'client_video';
  const isFestival = effectivePlacement?.includes('festival') || effectivePlacement?.includes('event');
  const adTag = isFestival ? 'EVENT' : 'SPONSORED';

  return (
    <div className="space-y-4">
      {/* Template cycler — only shown when 2+ templates selected */}
      {campaignTypes.length > 1 && currentTypeDef && (
        <div className="flex items-center justify-between bg-gray-900/60 border border-gray-700/60 rounded-xl px-3 py-2">
          <button
            onClick={() => setPreviewIndex((i) => (i - 1 + campaignTypes.length) % campaignTypes.length)}
            className="p-1 rounded text-gray-500 hover:text-white hover:bg-gray-800 transition-colors"
            title="Previous template"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <div className="flex-1 text-center min-w-0">
            <p className="text-[10px] text-gray-500 uppercase tracking-wider">
              Previewing {previewIndex + 1} of {campaignTypes.length}
            </p>
            <p className="text-xs text-emerald-400 font-semibold truncate flex items-center justify-center gap-1">
              <span>{currentTypeDef.icon}</span>
              {currentTypeDef.label}
            </p>
          </div>
          <button
            onClick={() => setPreviewIndex((i) => (i + 1) % campaignTypes.length)}
            className="p-1 rounded text-gray-500 hover:text-white hover:bg-gray-800 transition-colors"
            title="Next template"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* View switcher */}
      <div className="flex gap-1 rounded-xl p-0.5 bg-gray-900/80 border border-gray-700/50">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-semibold transition-all flex-1 justify-center ${
              tab === t.key
                ? 'bg-gray-700 text-white shadow-sm'
                : 'text-gray-500 hover:text-gray-300'
            }`}
          >
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* Phone frame */}
      <div className="relative mx-auto" style={{ width: 270 }}>
        {/* Outer bezel — silver device shell */}
        <div
          className="rounded-[36px] p-[3px] shadow-2xl"
          style={{
            background: 'linear-gradient(145deg, #4a5568 0%, #2d3748 40%, #1a202c 100%)',
            boxShadow: '0 30px 60px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.08) inset',
          }}
        >
          {/* Inner bezel */}
          <div className="bg-[#0d0f14] rounded-[33px] p-2">
            {/* Notch / Dynamic Island */}
            <div className="flex justify-center mb-1.5">
              <div className="w-24 h-5 bg-[#0d0f14] rounded-b-2xl flex items-center justify-center gap-3">
                <div className="w-2 h-2 rounded-full bg-[#1c1f26]" />
                <div className="w-10 h-3.5 rounded-full bg-[#1c1f26]" />
              </div>
            </div>

            {/* Screen — flex column so map tab gets real height for Leaflet */}
            <div
              className="rounded-[26px] overflow-hidden flex flex-col"
              style={{ height: 468, background: '#111827' }}
            >
              {/* Status bar */}
              <div className="shrink-0 flex items-center justify-between px-4 pt-1.5 pb-1 text-[10px] text-gray-400">
                <span className="font-semibold">9:41</span>
                <div className="flex items-center gap-1.5">
                  <svg width="14" height="10" viewBox="0 0 14 10" fill="none">
                    <rect x="0" y="3" width="3" height="7" rx="0.5" fill="#9ca3af" />
                    <rect x="4" y="2" width="3" height="8" rx="0.5" fill="#9ca3af" />
                    <rect x="8" y="0" width="3" height="10" rx="0.5" fill="#9ca3af" />
                    <rect x="12" y="0" width="2" height="10" rx="0.5" fill="#374151" />
                  </svg>
                  <svg width="14" height="10" viewBox="0 0 14 10" fill="none">
                    <rect x="1" y="2" width="11" height="7" rx="1.5" stroke="#9ca3af" strokeWidth="1.2" />
                    <rect x="12" y="4" width="2" height="3" rx="0.5" fill="#9ca3af" />
                    <rect x="2" y="3" width="8" height="5" rx="0.5" fill="#10b981" />
                  </svg>
                </div>
              </div>

              <div className="flex-1 min-h-0 overflow-hidden">
              {/* Screen content — swaps by tab */}
              {tab === 'passenger' && (
                <PassengerScreen
                  brandName={brandName} logoUrl={logoUrl} heroUrl={heroUrl}
                  headline={headline} body={body} ctaText={ctaText}
                  bg={bg} fg={fg} adTag={adTag}
                  isFullBleed={isFullBleed} isVideo={isVideo} videoUrl={uploadVideoUrl}
                  placement={effectivePlacement}
                />
              )}
              {tab === 'driver' && (
                <DriverScreen brandName={brandName} logoUrl={logoUrl} headline={headline} bg={bg} fg={fg} />
              )}
              {tab === 'pin' && (
                <PinScreen
                  mode={mode}
                  brandName={brandName} logoUrl={logoUrl} heroUrl={heroUrl} bg={bg}
                />
              )}
              </div>
            </div>
          </div>
        </div>

        {/* Home indicator */}
        <div className="flex justify-center mt-1.5">
          <div className="w-24 h-1 bg-gray-600 rounded-full" />
        </div>
      </div>

      {/* Pin coordinate readout — only on the Map Pin tab. Drives parity
          with the draggable marker inside the phone preview. The
          operator can either drag the marker on the live preview map
          OR copy/paste explicit coords here, e.g. from Google Maps. */}
      {tab === 'pin' && (
        <PinCoordReadout
          mode={mode}
          businessLat={selectedBusiness?.latitude ?? null}
          businessLng={selectedBusiness?.longitude ?? null}
          overrideLat={pinLatOverride}
          overrideLng={pinLngOverride}
        />
      )}

      {/* Placement label */}
      {templatePlacement && tab !== 'pin' && (
        <div className="flex items-center gap-2 bg-gray-800/50 border border-gray-700/50 rounded-lg px-3 py-2">
          <Zap className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
          <div>
            <p className="text-[10px] text-gray-500 uppercase tracking-wider">Placement</p>
            <p className="text-xs text-white font-medium capitalize">
              {templatePlacement.replace(/_/g, ' ')}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Pin coordinate readout (lives below the phone frame) ─────────────────

function PinCoordReadout({
  mode, businessLat, businessLng, overrideLat, overrideLng,
}: {
  mode: WizardMode;
  businessLat: number | null;
  businessLng: number | null;
  overrideLat: number | null;
  overrideLng: number | null;
}) {
  const store = useWizardStore(mode);

  const lat = overrideLat ?? businessLat ?? 15.301;
  const lng = overrideLng ?? businessLng ?? -61.388;
  const overridden =
    businessLat !== null &&
    businessLng !== null &&
    overrideLat !== null &&
    overrideLng !== null &&
    (Math.abs(overrideLat - businessLat) > 0.00005 ||
      Math.abs(overrideLng - businessLng) > 0.00005);

  return (
    <div className="bg-gray-900/60 border border-gray-700/60 rounded-xl p-3 space-y-2.5">
      <div className="flex items-center gap-2">
        <Crosshair className="w-3.5 h-3.5 text-emerald-400" />
        <p className="text-[11px] uppercase tracking-wider text-gray-400 font-semibold">
          Pin position
        </p>
        {overridden && (
          <span className="ml-auto text-[10px] text-amber-300 bg-amber-500/10 border border-amber-500/30 px-1.5 py-0.5 rounded">
            overridden
          </span>
        )}
      </div>

      <p className="text-[11px] text-gray-500 leading-snug">
        Drag the marker on the map preview to override where this sponsored pin lands.
        The override is what gets published. The brand's registered Google coords
        are not changed.
      </p>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-[10px] text-gray-500 uppercase tracking-wide">Latitude</label>
          <input
            type="number"
            step="0.000001"
            value={lat.toFixed(6)}
            onChange={(e) => {
              const v = parseFloat(e.target.value);
              if (!Number.isNaN(v)) store.set({ pinLatOverride: v });
            }}
            className="w-full bg-gray-900 border border-gray-700 rounded text-white text-xs font-mono px-2 py-1.5"
          />
        </div>
        <div>
          <label className="text-[10px] text-gray-500 uppercase tracking-wide">Longitude</label>
          <input
            type="number"
            step="0.000001"
            value={lng.toFixed(6)}
            onChange={(e) => {
              const v = parseFloat(e.target.value);
              if (!Number.isNaN(v)) store.set({ pinLngOverride: v });
            }}
            className="w-full bg-gray-900 border border-gray-700 rounded text-white text-xs font-mono px-2 py-1.5"
          />
        </div>
      </div>

      <div className="flex items-center gap-2">
        <button
          onClick={() => {
            store.set({
              pinLatOverride: businessLat ?? 15.301,
              pinLngOverride: businessLng ?? -61.388,
            });
          }}
          disabled={!overridden}
          className="flex items-center gap-1 text-[11px] text-gray-400 hover:text-white border border-gray-700 hover:border-gray-500 disabled:opacity-30 disabled:cursor-not-allowed rounded-md px-2 py-1 transition-colors"
        >
          <RotateCcw className="w-3 h-3" />
          {businessLat ? 'Reset to brand location' : 'Reset to Roseau center'}
        </button>
        <a
          href={`https://www.google.com/maps?q=${lat.toFixed(6)},${lng.toFixed(6)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[11px] text-emerald-400 hover:text-emerald-300 underline"
        >
          Open in Google Maps
        </a>
      </div>
    </div>
  );
}

// ─── Passenger screen ────────────────────────────────────────────────────────

function PassengerScreen({
  brandName, logoUrl, heroUrl, headline, body, ctaText,
  bg, fg, adTag, isFullBleed, isVideo, videoUrl, placement,
}: {
  brandName: string; logoUrl: string; heroUrl: string;
  headline: string; body: string; ctaText: string;
  bg: string; fg: string; adTag: string;
  isFullBleed: boolean; isVideo: boolean; videoUrl: string;
  placement?: string;
}) {
  const isSheetCarousel = Boolean(placement?.startsWith('passenger_sheet_'));
  if (placement === 'splash_takeover') {
    const onImage = Boolean(heroUrl);
    const textColor = onImage ? '#ffffff' : fg;
    return (
      <div className="h-full relative flex flex-col" style={{ backgroundColor: bg }}>
        {onImage && (
          <>
            <img src={heroUrl} alt="" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-x-0 top-0 h-16 bg-black/30" />
            <div className="absolute inset-x-0 bottom-0 h-1/2 bg-black/50" />
          </>
        )}
        <div className="relative flex items-center justify-between px-3 pt-3">
          <span className="px-2 py-0.5 rounded-full text-[8px] font-extrabold uppercase tracking-wider bg-white/90 text-gray-900">
            Sponsored
          </span>
          <span className="px-3 py-1 rounded-full text-[10px] font-bold text-white bg-gray-900/60 border border-white/30">
            Skip 6
          </span>
        </div>
        {!onImage && logoUrl ? (
          <div className="relative flex-1 flex items-center justify-center">
            <img src={logoUrl} alt="" className="w-20 h-20 rounded-2xl bg-white object-contain" />
          </div>
        ) : (
          <div className="flex-1" />
        )}
        <div className="relative px-4 pb-5 space-y-1">
          <p className="text-[8px] font-bold uppercase tracking-wider opacity-80" style={{ color: textColor }}>
            Presented by {brandName}
          </p>
          <p className="text-base font-extrabold leading-tight line-clamp-3" style={{ color: textColor }}>
            {headline || 'Your headline here'}
          </p>
          {body && (
            <p className="text-[10px] leading-snug line-clamp-3 opacity-80" style={{ color: textColor }}>
              {body}
            </p>
          )}
          {ctaText && (
            <div className="mt-2 rounded-xl bg-white text-gray-900 text-[11px] font-extrabold text-center py-2.5">
              {ctaText}
            </div>
          )}
        </div>
      </div>
    );
  }
  if (isVideo && videoUrl) {
    return (
      <div className="h-full flex flex-col">
        <div className="flex-1 relative bg-black">
          <video src={videoUrl} className="w-full h-full object-cover" muted autoPlay loop playsInline />
          <div className="absolute top-2 left-2">
            <span className="px-1.5 py-0.5 rounded text-[8px] font-bold bg-black/40 text-white/90 backdrop-blur-sm">{adTag}</span>
          </div>
          <button className="absolute top-2 right-2 w-6 h-6 bg-black/40 backdrop-blur-sm rounded-full flex items-center justify-center">
            <X className="w-3 h-3 text-white" />
          </button>
          <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/90 via-black/50 to-transparent">
            {logoUrl && <img src={logoUrl} alt="" className="w-8 h-8 rounded-full border-2 border-white/30 mb-1.5 object-cover" />}
            <p className="text-white text-xs font-bold leading-tight">{headline || brandName}</p>
            {ctaText && (
              <div className="mt-2 inline-flex items-center gap-1 bg-white text-gray-900 rounded-full px-3 py-1 text-[10px] font-bold">
                {ctaText} <ChevronRight className="w-3 h-3" />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (isFullBleed && heroUrl) {
    return (
      <div className="h-full relative">
        <img src={heroUrl} alt="" className="w-full h-full object-cover" />
        <div className="absolute top-2 left-2">
          <span className="px-1.5 py-0.5 rounded text-[8px] font-bold bg-black/40 text-white/90 backdrop-blur-sm">{adTag}</span>
        </div>
        <button className="absolute top-2 right-2 w-6 h-6 bg-black/40 backdrop-blur-sm rounded-full flex items-center justify-center">
          <X className="w-3 h-3 text-white" />
        </button>
        <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/95 via-black/60 to-transparent">
          <p className="text-white text-sm font-bold leading-tight">{headline || brandName}</p>
          {body && <p className="text-white/70 text-[10px] mt-1 line-clamp-2">{body}</p>}
          {ctaText && (
            <div className="mt-2.5 inline-flex items-center gap-1 bg-white text-gray-900 rounded-full px-3 py-1 text-[10px] font-bold">
              {ctaText} <ChevronRight className="w-3 h-3" />
            </div>
          )}
        </div>
      </div>
    );
  }

  // Real passenger home: a full-bleed map with a floating sponsor strip
  // pinned to the top, then a search/route sheet at the bottom. We mirror
  // that layout so the operator sees how their ad ACTUALLY composes with
  // the live UI.
  return (
    <div className="h-full flex flex-col bg-[#0f172a]">
      {/* App top bar */}
      <div className="px-4 py-2 flex items-center justify-between border-b border-gray-800/60">
        <span className="text-white text-xs font-bold tracking-tight">H-Plate</span>
        <div className="w-6 h-6 rounded-full bg-gray-700 border border-gray-600" />
      </div>

      {/* Map background */}
      <div className="relative flex-1 overflow-hidden">
        <PassengerMapBg />

        {/* Floating sponsor strip — what `<PassengerHomeSponsorSlot>` renders */}
        <div className="absolute top-2 left-2 right-2 z-[400]">
          <div
            className="rounded-2xl overflow-hidden shadow-2xl"
            style={{ backgroundColor: bg, border: `1px solid ${lightenHex(bg, 40)}30` }}
          >
            {heroUrl ? (
              <div className="relative h-24">
                <img src={heroUrl} alt="" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/30" />
                <div className="absolute top-2 left-2">
                  <span
                    className="px-1.5 py-0.5 rounded text-[8px] font-bold"
                    style={{ backgroundColor: `${bg}cc`, color: fg }}
                  >
                    {adTag}
                  </span>
                </div>
                <button className="absolute top-2 right-2 w-5 h-5 rounded-full bg-black/30 backdrop-blur-sm flex items-center justify-center">
                  <X className="w-3 h-3 text-white/70" />
                </button>
              </div>
            ) : null}

            <div className="p-2.5">
              <div className="flex items-center gap-2 mb-1.5">
                {logoUrl ? (
                  <img src={logoUrl} alt="" className="w-8 h-8 rounded-full border-2 object-cover" style={{ borderColor: `${fg}20` }} />
                ) : (
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0"
                    style={{ backgroundColor: lightenHex(bg, 35), color: fg }}
                  >
                    {brandName.charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] font-bold truncate" style={{ color: fg }}>{brandName}</p>
                  {!heroUrl && (
                    <span
                      className="text-[8px] font-bold px-1.5 py-0.5 rounded"
                      style={{ backgroundColor: `${fg}18`, color: `${fg}90` }}
                    >
                      {adTag}
                    </span>
                  )}
                </div>
                {heroUrl && (
                  <button className="w-5 h-5 rounded-full bg-black/20 flex items-center justify-center flex-shrink-0">
                    <X className="w-3 h-3" style={{ color: `${fg}70` }} />
                  </button>
                )}
              </div>

              <p className="text-[12px] font-bold leading-tight" style={{ color: fg }}>
                {headline || 'Your headline here'}
              </p>
              {body && (
                <p className="text-[10px] mt-0.5 opacity-75 line-clamp-2 leading-snug" style={{ color: fg }}>
                  {body}
                </p>
              )}
              {ctaText && (
                <div
                  className="mt-2 inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[9px] font-bold"
                  style={{ backgroundColor: `${fg}22`, color: fg }}
                >
                  {ctaText} <ChevronRight className="w-2.5 h-2.5" />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Bottom sheet — expanded when previewing in-sheet carousel placements */}
        <div className="absolute bottom-0 left-0 right-0 z-[400]">
          <div
            className={`bg-[#111827]/95 backdrop-blur-md border-t border-gray-700/60 shadow-2xl ${
              isSheetCarousel ? 'rounded-t-2xl px-3 pt-2.5 pb-2' : 'mx-2 mb-2 rounded-2xl border px-3 py-2.5'
            }`}
          >
            <div className="flex items-center gap-2 mb-2">
              <div className="w-7 h-7 rounded-full bg-emerald-500/15 flex items-center justify-center shrink-0">
                <MapPin className="w-3.5 h-3.5 text-emerald-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[9px] text-gray-500 uppercase tracking-wider">
                  {placement?.includes('confirm')
                    ? 'Confirm ride'
                    : placement?.includes('active')
                      ? 'Your ride'
                      : 'Where to?'}
                </p>
                <p className="text-[11px] text-gray-300 font-medium truncate">
                  {placement?.includes('confirm')
                    ? 'Roseau → Portsmouth'
                    : placement?.includes('active')
                      ? 'Driver on the way'
                      : 'Pick a destination'}
                </p>
              </div>
            </div>

            {isSheetCarousel ? (
              <>
                <p className="text-[8px] text-gray-500 uppercase tracking-wider mb-1.5 px-0.5">
                  Offers while you plan
                </p>
                <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-none">
                  {[0, 1].map((i) => (
                    <div
                      key={i}
                      className="shrink-0 w-[72%] rounded-xl overflow-hidden border border-gray-700/50"
                      style={{ backgroundColor: bg }}
                    >
                      <div className="p-2">
                        <span className="text-[7px] font-bold px-1 py-0.5 rounded bg-black/30 text-white/90">
                          {adTag}
                        </span>
                        <p className="text-[10px] font-bold mt-1 leading-tight truncate" style={{ color: fg }}>
                          {headline || brandName}
                        </p>
                        {body && (
                          <p className="text-[8px] mt-0.5 line-clamp-2 opacity-80" style={{ color: fg }}>
                            {body}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : null}
          </div>
        </div>
      </div>

      {/* Bottom nav */}
      <div className="flex items-center justify-around px-4 py-1.5 border-t border-gray-800 bg-[#0f172a]">
        {[
          { label: 'Home', active: true },
          { label: 'Map', active: false },
          { label: 'Rides', active: false },
          { label: 'Profile', active: false },
        ].map((item) => (
          <div key={item.label} className="flex flex-col items-center gap-0.5">
            <div className={`w-5 h-5 rounded-md ${item.active ? 'bg-emerald-500/30' : 'bg-gray-800'}`} />
            <span className={`text-[8px] ${item.active ? 'text-emerald-400' : 'text-gray-600'}`}>
              {item.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Passenger map background ─────────────────────────────────────────────
//
// Renders a static PNG of the Roseau region using OSM tiles via a single
// composite tile URL — keeps the bundle small and prevents the preview
// from triggering 50+ Leaflet tile requests for every wizard repaint.
function PassengerMapBg() {
  return (
    <div className="absolute inset-0">
      {/* Static OSM tile from Carto dark — covers Roseau & the West coast.
          z=12 so it renders an island-level view.
          Single tile = 1 network request, perfect for a preview. */}
      <img
        src="https://a.basemaps.cartocdn.com/dark_all/12/1162/1797.png"
        alt=""
        className="w-full h-full object-cover opacity-90"
        loading="lazy"
      />
      {/* Subtle dark vignette to simulate the depth of the real map */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/0 via-black/10 to-black/40" />
    </div>
  );
}

// ─── Driver screen ────────────────────────────────────────────────────────────

function DriverScreen({
  brandName, logoUrl, headline, bg, fg,
}: {
  brandName: string; logoUrl: string; headline: string; bg: string; fg: string;
}) {
  // Mirrors the real driver home: compact top map (~32% of screen) with
  // the route polyline, then the trip manifest / sponsor strip / quick
  // actions stacked below. The sponsor card we render on the right is
  // exactly what `<DriverIdleSponsorSlot variant="panel">` produces in
  // the actual app.
  return (
    <div className="h-full flex flex-col bg-[#0a0f1a]">
      {/* Header bar */}
      <div className="px-3.5 py-2 flex items-center justify-between bg-[#111827] border-b border-gray-800/80">
        <span className="text-white text-xs font-bold">H-Plate Driver</span>
        <div className="flex items-center gap-1.5">
          <div className="w-2 h-2 rounded-full bg-emerald-400" />
          <span className="text-[10px] text-emerald-400 font-semibold">Online</span>
        </div>
      </div>

      {/* Compact map (top third) — real OSM tile + a fake route line */}
      <div className="relative h-[34%] overflow-hidden" style={{ background: '#0f1f2e' }}>
        <img
          src="https://a.basemaps.cartocdn.com/dark_all/12/1162/1798.png"
          alt=""
          className="w-full h-full object-cover opacity-90"
          loading="lazy"
        />
        <svg className="absolute inset-0 w-full h-full" viewBox="0 0 270 120" fill="none" preserveAspectRatio="none">
          <path
            d="M 30 100 Q 90 60 150 50 Q 210 40 250 25"
            stroke="#10b981"
            strokeWidth="3"
            strokeLinecap="round"
            opacity="0.95"
          />
          <circle cx="30" cy="100" r="4" fill="#10b981" />
          <circle cx="250" cy="25" r="4" fill="#facc15" />
        </svg>
        <div className="absolute top-1.5 right-1.5 bg-black/60 backdrop-blur-sm rounded-md px-1.5 py-0.5">
          <span className="text-[9px] text-emerald-400 font-bold">5 km · 12 min</span>
        </div>
      </div>

      {/* Trip context strip */}
      <div className="px-3 py-2 bg-[#0f172a] border-b border-gray-800/60 flex items-center gap-2">
        <div className="w-7 h-7 rounded-full bg-emerald-500/15 flex items-center justify-center shrink-0">
          <Car className="w-3.5 h-3.5 text-emerald-400" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] text-gray-500 uppercase tracking-wider">Next pickup</p>
          <p className="text-[11px] text-gray-300 font-medium truncate">Roseau Market → Marigot</p>
        </div>
        <span className="text-[10px] font-bold text-emerald-400 bg-emerald-500/15 px-2 py-0.5 rounded-full">
          XCD 12
        </span>
      </div>

      {/* Driver-side sponsor panel (the placement under audit) */}
      <div className="flex-1 px-3 pt-2.5">
        <p className="text-[9px] text-gray-500 uppercase tracking-wider mb-1.5 flex items-center gap-1">
          <Zap className="w-2.5 h-2.5" /> Sponsored break
        </p>
        <div
          className="rounded-xl p-3 flex items-center gap-2.5 shadow-lg"
          style={{ backgroundColor: bg, border: `1px solid ${lightenHex(bg, 30)}50` }}
        >
          {logoUrl ? (
            <img src={logoUrl} alt="" className="w-10 h-10 rounded-full border-2 object-cover shrink-0" style={{ borderColor: `${fg}25` }} />
          ) : (
            <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 text-xs font-bold"
              style={{ backgroundColor: lightenHex(bg, 30), color: fg }}>
              {brandName.charAt(0)}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-[11px] font-bold truncate" style={{ color: fg }}>{brandName}</p>
            <p className="text-[10px] opacity-70 truncate" style={{ color: fg }}>
              {headline || 'Sponsored message'}
            </p>
          </div>
          <ChevronRight className="w-4 h-4 shrink-0" style={{ color: `${fg}80` }} />
        </div>
        <p className="text-[9px] text-gray-600 mt-1.5 italic">
          Visible to drivers when idle / awaiting their next pickup.
        </p>
      </div>

      {/* Quick actions */}
      <div className="flex items-center justify-around py-1.5 px-3 bg-[#111827] border-t border-gray-800">
        {['Rides', 'Nav', 'Earnings', 'More'].map((label, i) => (
          <div key={label} className="flex flex-col items-center gap-0.5">
            <div className={`w-6 h-6 rounded-lg ${i === 0 ? 'bg-emerald-500/20' : 'bg-gray-800'}`} />
            <span className={`text-[8px] ${i === 0 ? 'text-emerald-400' : 'text-gray-600'}`}>{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Map pin screen — real OSM tiles + draggable marker ───────────────────

function PinScreen({
  mode, brandName, logoUrl, heroUrl, bg,
}: {
  mode: WizardMode;
  brandName: string; logoUrl: string; heroUrl: string; bg: string;
}) {
  const store = useWizardStore(mode);
  const { selectedBusiness, pinLatOverride, pinLngOverride, pinStyle } = store;

  const lat = pinLatOverride ?? selectedBusiness?.latitude ?? 15.301;
  const lng = pinLngOverride ?? selectedBusiness?.longitude ?? -61.388;

  // Resolve the active pin style. Wizard store's `pinStyle` wins; if
  // unset we derive from the brand's category. The marker, the small
  // circular badge in the info card, and the category label all use
  // this so the preview is consistent with what the mobile clients
  // will render.
  const resolvedStyle = resolvePinStyle(pinStyle, selectedBusiness?.category ?? null);
  const StyleIcon = resolvedStyle.icon;

  return (
    <div className="h-full min-h-0 flex flex-col bg-[#111827]">
      {/* App header — mirrors the actual passenger home top bar */}
      <div className="shrink-0 px-4 py-2.5 flex items-center justify-between border-b border-gray-800">
        <span className="text-white text-xs font-bold">H-Plate</span>
        <div className="flex items-center gap-1.5">
          <MapPin className="w-3.5 h-3.5 text-emerald-400" />
          <span className="text-[10px] text-gray-400 truncate max-w-[110px]">
            {selectedBusiness?.village || 'Roseau'}
          </span>
        </div>
      </div>

      {/* Real map area + draggable branded marker */}
      <div className="flex-1 min-h-0 relative overflow-hidden">
        <DraggableLeafletPin
          mode={mode}
          lat={lat}
          lng={lng}
          brandName={brandName}
          logoUrl={logoUrl}
          style={resolvedStyle}
        />

        {/* "Drag me" coachmark — fades out after first drag (managed by
            the leaflet hook, but rendered here as a non-interactive
            overlay so the operator immediately understands the marker
            is interactive). Click-through pointer-events let the map
            still receive the drag. */}
        <div
          className="absolute top-2 left-1/2 -translate-x-1/2 z-[400] pointer-events-none"
        >
          <div className="bg-emerald-500/95 text-white text-[10px] font-bold px-2 py-1 rounded-full shadow-lg flex items-center gap-1.5">
            <Crosshair className="w-3 h-3" />
            Drag pin to adjust
          </div>
        </div>

        {/* Info card at bottom — mirrors what the passenger sees when
            tapping a featured business pin in the actual app. */}
        <div className="absolute bottom-2 left-2 right-2 z-[400] pointer-events-none">
          <div className="bg-[#111827]/95 backdrop-blur-sm rounded-xl border border-gray-700/60 overflow-hidden shadow-2xl">
            {heroUrl && (
              <img src={heroUrl} alt="" className="w-full h-16 object-cover" />
            )}
            <div className="p-2.5 flex items-center gap-2.5">
              {logoUrl ? (
                <img src={logoUrl} alt="" className="w-9 h-9 rounded-full border-2 border-white/90 object-cover shrink-0" style={{ boxShadow: `0 0 0 1px ${resolvedStyle.bg}66` }} />
              ) : (
                <div
                  className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 border-2 border-white/90"
                  style={{ backgroundColor: resolvedStyle.bg, boxShadow: `0 0 0 1px ${resolvedStyle.bg}66` }}
                >
                  <StyleIcon className="w-4 h-4" style={{ color: resolvedStyle.fg }} />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-white text-xs font-bold truncate">{brandName}</p>
                <div className="flex items-center gap-1 mt-0.5">
                  <span
                    className="text-[8px] font-bold uppercase tracking-wide px-1 py-0.5 rounded"
                    style={{ backgroundColor: `${resolvedStyle.bg}22`, color: resolvedStyle.bg }}
                  >
                    {resolvedStyle.label}
                  </span>
                  <span className="text-[9px] text-gray-500 font-mono">
                    {lat.toFixed(4)}, {lng.toFixed(4)}
                  </span>
                </div>
              </div>
              <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 text-[8px] px-1.5 shrink-0">
                FEATURED
              </Badge>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom nav */}
      <div className="shrink-0 flex items-center justify-around px-4 py-2 border-t border-gray-800 bg-[#111827]">
        {['Home', 'Map', 'Rides', 'Me'].map((label, i) => (
          <div key={label} className="flex flex-col items-center gap-0.5">
            <div className={`w-5 h-5 rounded-md ${i === 1 ? 'bg-emerald-500/20' : 'bg-gray-800'}`} />
            <span className={`text-[8px] ${i === 1 ? 'text-emerald-400' : 'text-gray-600'}`}>{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Leaflet OSM map with draggable branded marker ────────────────────────

/** OSM tiles — same provider as BrandPicker; reliable for Dominica preview. */
const WIZARD_MAP_TILES = {
  url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
  attribution: '&copy; OpenStreetMap',
};

function DraggableLeafletPin({
  mode, lat, lng, brandName, logoUrl, style,
}: {
  mode: WizardMode;
  lat: number;
  lng: number;
  brandName: string;
  logoUrl: string;
  style: PinStyle;
}) {
  const store = useWizardStore(mode);
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const markerRef = useRef<any>(null);
  const [mapReady, setMapReady] = useState(false);

  // Build the inner contents of the pin — Glanceable Pin style:
  //   • If the brand has a logo, sit it on a WHITE inner badge so the
  //     artwork reads (KFC's red-on-white, etc.). The category colour
  //     becomes the OUTER RING + tail, preserving the category cue.
  //   • If no logo, render the category icon on a category-coloured
  //     badge — the fallback we already had.
  // Used both at map init AND when the style/logo changes later
  // (operator clicks a different pin style after the map is mounted).
  const buildIconHtml = () => {
    const color = style.bg;
    const fg = style.fg;
    const svgPath = PIN_STYLE_SVG[style.key];

    const innerBg = logoUrl ? '#ffffff' : color;
    const inner = logoUrl
      ? `<img src="${logoUrl}" style="width:100%;height:100%;object-fit:contain;padding:2px;" />`
      : `<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="${fg}" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="${svgPath}"/></svg>`;

    return `
      <div style="display:flex;flex-direction:column;align-items:center;filter:drop-shadow(0 6px 12px rgba(0,0,0,0.45));cursor:grab;" aria-label="${brandName} (${style.label})">
        <div style="
          position:relative;
          width:42px;height:42px;border-radius:50%;
          background:${color};
          display:flex;align-items:center;justify-content:center;
          box-shadow: 0 0 0 2px ${color}55, 0 6px 20px ${color}66;
        ">
          <div style="
            width:30px;height:30px;border-radius:50%;
            background:${innerBg};
            display:flex;align-items:center;justify-content:center;
            overflow:hidden;
          ">${inner}</div>
          <!-- Sponsored sparkle in the corner -->
          <div style="
            position:absolute;top:-3px;right:-3px;
            width:14px;height:14px;border-radius:50%;
            background:#f59e0b;border:1.5px solid #fff;
            display:flex;align-items:center;justify-content:center;
          ">
            <svg viewBox="0 0 24 24" width="8" height="8" fill="#fff">
              <path d="m12 3-1.9 5.8a2 2 0 0 1-1.3 1.3L3 12l5.8 1.9a2 2 0 0 1 1.3 1.3L12 21l1.9-5.8a2 2 0 0 1 1.3-1.3L21 12l-5.8-1.9a2 2 0 0 1-1.3-1.3Z"/>
            </svg>
          </div>
        </div>
        <div style="
          width:0;height:0;
          border-left:7px solid transparent;border-right:7px solid transparent;
          border-top:10px solid ${color};
          margin-top:-2px;
        "></div>
      </div>
    `;
  };

  // Map init when the Map Pin tab mounts. OSM tiles + resize observer so
  // the phone frame always gives Leaflet a non-zero area.
  useEffect(() => {
    let cancelled = false;
    const container = containerRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver(() => {
      mapRef.current?.invalidateSize();
    });

    (async () => {
      const L = await import('leaflet');
      await import('leaflet/dist/leaflet.css');
      if (cancelled || !containerRef.current) return;

      const map = L.map(containerRef.current, {
        center: [lat, lng],
        zoom: 14,
        scrollWheelZoom: true,
        zoomControl: true,
        attributionControl: true,
      });

      L.tileLayer(WIZARD_MAP_TILES.url, {
        attribution: WIZARD_MAP_TILES.attribution,
        maxZoom: 19,
      }).addTo(map);

      const icon = L.divIcon({
        html: buildIconHtml(),
        className: 'wizard-pin-divicon',
        iconSize: [42, 56],
        iconAnchor: [21, 56],
      });

      const marker = L.marker([lat, lng], { draggable: true, icon }).addTo(map);
      marker.on('dragend', () => {
        const pos = marker.getLatLng();
        store.set({
          pinLatOverride: Math.round(pos.lat * 1e6) / 1e6,
          pinLngOverride: Math.round(pos.lng * 1e6) / 1e6,
        });
      });

      mapRef.current = map;
      markerRef.current = marker;
      resizeObserver.observe(containerRef.current);

      requestAnimationFrame(() => {
        map.invalidateSize();
        map.setView([lat, lng], map.getZoom(), { animate: false });
      });
      setTimeout(() => map.invalidateSize(), 150);
      setTimeout(() => map.invalidateSize(), 400);

      if (!cancelled) setMapReady(true);
    })();

    return () => {
      cancelled = true;
      setMapReady(false);
      resizeObserver.disconnect();
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        markerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync external coordinate edits (the readout panel below the phone)
  // to the marker so dragging vs. typing both stay in lock-step.
  useEffect(() => {
    if (!markerRef.current || !mapRef.current) return;
    const pos = markerRef.current.getLatLng();
    if (Math.abs(pos.lat - lat) > 0.0001 || Math.abs(pos.lng - lng) > 0.0001) {
      markerRef.current.setLatLng([lat, lng]);
      mapRef.current.panTo([lat, lng], { animate: true, duration: 0.4 });
    }
  }, [lat, lng]);

  // Re-render the icon when the brand info or pin style changes (the
  // operator picks a different category in the picker, uploads a logo,
  // edits the brand name, etc.). We re-import leaflet here only for
  // the divIcon factory — the map itself is already mounted.
  useEffect(() => {
    if (!markerRef.current) return;
    let active = true;
    (async () => {
      const L = await import('leaflet');
      if (!active) return;
      markerRef.current.setIcon(
        L.divIcon({
          html: buildIconHtml(),
          className: '',
          iconSize: [42, 56],
          iconAnchor: [21, 56],
        }),
      );
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandName, logoUrl, style.key, style.bg, style.fg]);

  return (
    <div className="absolute inset-0">
      <div
        ref={containerRef}
        className="absolute inset-0 z-0 [&_.leaflet-container]:!h-full [&_.leaflet-container]:!w-full [&_.leaflet-container]:!bg-[#1a2332]"
        style={{ background: '#1a2332' }}
      />
      {/* Subtle vignette so the preview still feels dark-themed */}
      <div
        className="absolute inset-0 z-[250] pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse at center, transparent 40%, rgba(15,23,42,0.55) 100%)',
        }}
      />
      {!mapReady && (
        <div className="absolute inset-0 z-[300] flex items-center justify-center bg-[#1a2332]/90 pointer-events-none">
          <span className="text-[10px] text-gray-400 font-medium">Loading map…</span>
        </div>
      )}
    </div>
  );
}
